import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-keys';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

interface ConsentTabProps {
  crawlId: string;
}

export function ConsentTab({ crawlId }: ConsentTabProps) {
  const query = useQuery({
    queryKey: queryKeys.crawls.consent(crawlId),
    queryFn: async () => {
      const { data, error } = await apiClient.GET('/crawls/{crawlId}/consent', {
        params: { path: { crawlId } },
      });
      if (error) throw error;
      return data;
    },
  });

  if (query.isLoading) return <p className="text-sm text-muted-foreground">Loading consent analysis…</p>;
  if (query.isError || !query.data) return <p className="text-sm text-red-600">Failed to load consent analysis.</p>;

  const analysis = query.data;

  return (
    <div className="grid gap-3">
      {analysis.perPage.length === 0 ? (
        <p className="text-sm text-muted-foreground">No consent data recorded for this crawl.</p>
      ) : (
        analysis.perPage.map((d) => (
          <Card key={d.pageId}>
            <CardHeader className="flex-row items-center justify-between">
              <CardTitle>Page {d.pageId.slice(0, 8)}…</CardTitle>
              <div className="flex items-center gap-2 text-xs">
                {d.provider && <Badge variant="info">{d.provider}</Badge>}
                {d.bannerFound ? <Badge variant="success">Banner found</Badge> : <Badge variant="muted">No banner</Badge>}
                {d.blocksScriptsBeforeConsent ? (
                  <Badge variant="success">Blocks scripts before consent</Badge>
                ) : (
                  <Badge variant="destructive">Scripts loaded before consent</Badge>
                )}
                {d.blocksCookiesBeforeConsent ? (
                  <Badge variant="success">Blocks cookies before consent</Badge>
                ) : (
                  <Badge variant="destructive">Cookies set before consent</Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {d.issues && d.issues.length > 0 && (
                <ul className="flex flex-col gap-1 text-xs">
                  {d.issues.map((iss, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <Badge variant={iss.severity === 'error' ? 'destructive' : iss.severity === 'warn' ? 'warning' : 'info'}>
                        {iss.severity}
                      </Badge>
                      <span className="text-muted-foreground">
                        <span className="font-mono">{iss.code}</span> — {iss.message}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
}
